// src/components/tickets/detail/CloseConfirm.tsx
//
// The close-confirm dialog (Story 4.3 AC 8). Closing names the consequence
// ("{rep first name} can reopen by replying within 30 days.") and confirms via
// `.btn-outline` — destructive is copy + confirmation, NEVER red fill
// (`.btn-danger` is retired; DESIGN.md). Overlay follows the repo idiom (fixed
// inset-0, role=dialog, Esc closes, focus restored by the caller).

import { useEffect, useRef } from 'react'

interface CloseConfirmProps {
  // The rep's first name for the reopen-consequence copy (null = no rep).
  repFirstName: string | null
  closing: boolean
  onConfirm: () => void
  onCancel: () => void
}

export function CloseConfirm({
  repFirstName,
  closing,
  onConfirm,
  onCancel,
}: CloseConfirmProps) {
  const confirmRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    confirmRef.current?.focus()
  }, [])

  const who = repFirstName ?? 'The rep'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-50 p-4"
      onKeyDown={e => {
        if (e.key === 'Escape') onCancel()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="close-confirm-title"
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg"
      >
        <h3
          id="close-confirm-title"
          className="mb-1 text-lg font-semibold text-gray-900"
        >
          Close this ticket?
        </h3>
        <p className="mb-4 text-sm text-gray-600">
          {who} can reopen by replying within 30 days.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={closing}
            className="btn-outline"
          >
            Cancel
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            disabled={closing}
            className="btn-outline"
          >
            {closing ? 'Closing…' : 'Close ticket'}
          </button>
        </div>
      </div>
    </div>
  )
}
